import { preprocessMarkdown } from "../markdownPreprocessor";
import { Source } from "../SourceReference";
import { processCodeBlocks, processSlackChannelMentions, processSlackLinks, processSlackSpecialMentions, processSlackSubteamMentions, processSlackUserMentions, processStepReferences, processToolReferences } from "../utils";

/**
 * Common preprocessing logic shared between Markdown component and clipboard processing
 * This adds backticks around special markers and calls preprocessMarkdown
 */
export function preprocessMarkdownContent(content: string): Promise<string> {
  // Convert Slack-formatted links and mentions to markdown BEFORE escapeNonHtmlTags runs
  // (inside preprocessMarkdown). escapeNonHtmlTags treats <https://...> as an unknown HTML
  // tag and escapes the angle brackets, which prevents processSlackLinks from matching later.
  let processedContent = processSlackLinks(content);
  processedContent = processSlackSubteamMentions(processedContent);
  processedContent = processSlackChannelMentions(processedContent);
  processedContent = processSlackUserMentions(processedContent);
  processedContent = processSlackSpecialMentions(processedContent);

  // Add backticks around †† if not already wrapped in backticks
  processedContent = processedContent.replace(
    /(?<!`)††([A-Za-z0-9_/]+)††(?!`)/g,
    "`††$1††`",
  );

  // Add backticks around TIME_MARKER if not already wrapped in backticks
  processedContent = processedContent.replace(
    /(?<!`)§([0-9TZ:-]+)§(?!`)/g,
    "`§$1§`",
  );

  // Enforce markdown is valid, or fix it if it's not
  return preprocessMarkdown(processedContent);
}

/**
 * Strips XML context wrappers injected by the agent system (InternalInstructions,
 * linear_issue_context, incident_channel_context, <<--..-->>) and returns just the
 * human-readable message text. Used by the chat page and the support agent
 * conversation detail so both render the same cleaned-up content.
 */
export function stripXmlContextTags(content: string): string {
  // Linear issue context — extract the triggering comment or issue title/description
  const linearMatch = content.match(/<linear_issue_context>([\s\S]*?)<\/linear_issue_context>/);
  if (linearMatch) {
    const innerXml = linearMatch[1];
    const primaryDirectiveMatch = innerXml.match(
      /<primary-directive-thread[^>]*>([\s\S]*?)<\/primary-directive-thread>/
    );
    if (primaryDirectiveMatch) {
      return primaryDirectiveMatch[1]
        .replace(/<comment[^>]*>/g, "")
        .replace(/<\/comment>/g, "")
        .replace(/<user[^>]*>[^<]*<\/user>\s*/g, "")
        .trim();
    }
    const titleMatch = innerXml.match(/<title>([\s\S]*?)<\/title>/);
    const descMatch = innerXml.match(/<description>([\s\S]*?)<\/description>/);
    const parts: string[] = [];
    if (titleMatch) parts.push(titleMatch[1].trim());
    if (descMatch && descMatch[1].trim() !== titleMatch?.[1]?.trim()) {
      parts.push(descMatch[1].trim());
    }
    return parts.join("\n\n") || content;
  }

  // InternalInstructions — strip the tag, keep the rest
  if (/<InternalInstructions[\s\S]*?<\/InternalInstructions>/.test(content)) {
    return content
      .replace(/<InternalInstructions(?:\s+type="[^"]*")?>([\s\S]*?)<\/InternalInstructions>/, "")
      .trim();
  }

  // Incident channel context — strip the wrapper, extract user_question if present
  if (/<incident_channel_context>[\s\S]*?<\/incident_channel_context>/.test(content)) {
    let cleaned = content.replace(/<incident_channel_context>[\s\S]*?<\/incident_channel_context>/, "").trim();
    const userQuestionMatch = cleaned.match(/<user_question>([\s\S]*?)<\/user_question>/);
    if (userQuestionMatch) {
      cleaned = userQuestionMatch[1].trim();
    }
    return cleaned;
  }

  // <<--JSON context-->> wrapper — strip it
  if (/<<--[\s\S]*?-->>/.test(content)) {
    return content.replace(/<<--[\s\S]*?-->>/, "").trim();
  }

  return content;
}

/**
 * Common processing chain shared between Markdown component and clipboard processing
 * This applies all the text transformations in the correct order
 */
export function processMarkdownText(text: string, sources: ReadonlyArray<Source> = []): string {
  let processedText = text;

  // Process Slack links
  processedText = processSlackLinks(processedText);

  // Process step references
  processedText = processStepReferences(processedText);

  // Process tool references
  processedText = processToolReferences(processedText, sources);

  // Fix code block formatting
  processedText = processCodeBlocks(processedText);

  // Process Slack mentions (order matters: specific patterns before general catch-all)
  processedText = processSlackSubteamMentions(processedText);
  processedText = processSlackChannelMentions(processedText);
  processedText = processSlackUserMentions(processedText);
  processedText = processSlackSpecialMentions(processedText);

  return processedText;
}